import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import Sidebar from './Sidebar';
import { LogOut, User, ChevronDown, RefreshCw, Bell } from 'lucide-react';
import { useQuery, useMutation, useQueryClient, useIsFetching } from '@tanstack/react-query';
import { notificationService } from '../api/notificationService';
import { uploadProfilePicture } from '../utils/s3Upload';
import ImageCropperModal from './ImageCropperModal';

const Layout = () => {
    const { user, logout, updateUserAttribute } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const isFetching = useIsFetching();
    const notificationPanelRef = useRef(null);
    const fileInputRef = useRef(null);

    // Lifted State for Division Selection (for DG Dashboard context)
    const [selectedDivision, setSelectedDivision] = useState(null);
    const [showProfile, setShowProfile] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [lastSynced, setLastSynced] = useState(new Date());
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);

    useEffect(() => {
        if (isFetching === 0) {
            setLastSynced(new Date());
        }
    }, [isFetching]);

    useEffect(() => {
        const onClickOutside = (event) => {
            if (notificationPanelRef.current && !notificationPanelRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const notificationsQuery = useQuery({
        queryKey: ['notifications', 'inbox'],
        queryFn: () => notificationService.getNotifications({ limit: 50, unreadOnly: false }),
        refetchInterval: 600000, // 10 minutes
        refetchOnWindowFocus: true,
        enabled: !!user,
    });

    const notificationItems = notificationsQuery.data?.items || [];
    const unreadCount = notificationsQuery.data?.unreadCount ?? notificationItems.filter(n => !n.IsRead).length;

    const markReadMutation = useMutation({
        mutationFn: (notificationId) => notificationService.markRead(notificationId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', 'inbox'] }),
    });

    const markAllReadMutation = useMutation({
        mutationFn: () => notificationService.markAllRead(),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications', 'inbox'] }),
    });

    const handleManualSync = () => {
        queryClient.invalidateQueries();
        queryClient.refetchQueries({ queryKey: ['notifications', 'inbox'], type: 'active' });
    };

    const handleSignOut = async () => {
        setShowProfile(false);
        try {
            await logout();
        } catch (error) {
            console.warn('Logout failed', error);
        } finally {
            localStorage.removeItem('accessToken');
            navigate('/login', { replace: true });
            setTimeout(() => {
                if (window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
            }, 100);
        }
    };

    const handleImageChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Load the image into the cropper
        const reader = new FileReader();
        reader.addEventListener('load', () => setCropImageSrc(reader.result));
        reader.readAsDataURL(file);

        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleCropSubmit = async (croppedFile) => {
        try {
            setIsUploadingImage(true);
            setIsUploadingImage(true);
            const s3Url = await uploadProfilePicture(croppedFile);

            const result = await updateUserAttribute('picture', s3Url);
            if (!result.success) {
                alert(`Failed to update profile picture: ${result.error}`);
            }
        } catch (error) {
            console.error("Image upload failed", error);
            alert("Failed to process and upload the image. Please try again.");
        } finally {
            setIsUploadingImage(false);
            setCropImageSrc(null); // Close the cropper
        }
    };

    const formatNotificationTime = (value) => {
        if (!value) return '';
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return '';
        return dt.toLocaleString('en-US', {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Asia/Kolkata'
        });
    };

    const getNotificationBadgeColor = (type) => {
        if ((type || '').includes('DEADLINE') || (type || '').includes('OVERDUE')) return '#dc2626';
        if ((type || '').includes('COMMENT')) return '#2563eb';
        return '#0f766e';
    };

    const handleNotificationClick = async (notification) => {
        try {
            if (!notification.IsRead) {
                await markReadMutation.mutateAsync(notification.NotificationID);
            }
        } catch (e) {
            console.warn('Failed to mark notification as read', e);
        }

        if (notification.EntityType === 'TASK' || notification.EntityType === 'SUBTASK') {
            navigate('/tasks');
        } else if (notification.EntityType === 'EVENT' || notification.EntityType === 'SUBEVENT') {
            navigate('/events');
        }
        setShowNotifications(false);
    };

    // Determine Central Title based on User Role (Identity-based)
    const groups = user?.roles || [];
    let centralTitle = '';

    const divisionGroup = groups.find(g => g.startsWith('EMPLOYEE_') || g.startsWith('COORDINATOR_'));
    const isDGUser = groups.includes('DG') || groups.includes('DG_OFFICE');

    if (isDGUser) {
        centralTitle = 'DG Office';
    } else if (divisionGroup) {
        centralTitle = divisionGroup.split('_')[1]; // Extracts 'AIC' from 'EMPLOYEE_AIC'
    }

    // Restore User Details for Profile Dropdown (Re-using logic/vars where possible)
    const userName = user?.attributes?.name || user?.username || 'User';
    const userProfile = user?.attributes?.profile || 'Employee';
    // Division Name for profile dropdown matches central title logic usually, but let's keep it consistent
    const divisionName = centralTitle;

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            fontFamily: 'sans-serif',
            backgroundColor: '#f8fafc'
        }}>
            {/* Top Navigation Bar */}
            <header style={{
                padding: '0 1.5rem', height: '64px', borderBottom: '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                zIndex: 50, position: 'relative',
                margin: '0.75rem 1rem 0.5rem 1rem',
                borderRadius: '18px'
            }}>
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                />
                {/* Left Side: Sync Button & Timestamp */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                            onClick={handleManualSync}
                            disabled={isFetching > 0}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '0.5rem',
                                background: 'transparent', border: '1px solid #cbd5e1',
                                color: isFetching > 0 ? '#94a3b8' : '#334155',
                                padding: '0.4rem 0.75rem', borderRadius: '6px',
                                cursor: isFetching > 0 ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem', fontWeight: 500,
                                transition: 'all 0.2s ease',
                                backgroundColor: isFetching > 0 ? '#f1f5f9' : 'transparent',
                            }}
                            onMouseEnter={(e) => { if (isFetching === 0) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                            onMouseLeave={(e) => { if (isFetching === 0) e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            <RefreshCw
                                size={16}
                                style={{
                                    animation: isFetching > 0 ? 'spin 1s linear infinite' : 'none',
                                    transformOrigin: 'center'
                                }}
                            />
                            <span>Sync</span>
                        </button>
                        <div style={{ fontSize: '0.7rem', color: '#64748b', display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                            <span style={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.65rem' }}>Last Synced</span>
                            <span style={{ fontWeight: 600, color: '#1e293b' }}>{lastSynced.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' })}</span>
                        </div>
                    </div>
                </div>

                {/* Centered User Identity Title */}
                {centralTitle && (
                    <div style={{
                        fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b',
                        position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap'
                    }}>
                        {centralTitle}
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ position: 'relative' }} ref={notificationPanelRef}>
                        <button
                            onClick={() => setShowNotifications(prev => !prev)}
                            style={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                border: '1px solid #cbd5e1',
                                background: showNotifications ? '#eff6ff' : '#ffffff',
                                cursor: 'pointer',
                            }}
                            title="Notifications"
                        >
                            <Bell size={18} color="#334155" />
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute',
                                    top: '-6px',
                                    right: '-6px',
                                    minWidth: '18px',
                                    height: '18px',
                                    borderRadius: '9px',
                                    background: '#ef4444',
                                    color: '#fff',
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 4px',
                                }}>
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </span>
                            )}
                        </button>

                        {showNotifications && (
                            <div style={{
                                position: 'absolute',
                                top: '44px',
                                right: 0,
                                width: '360px',
                                maxHeight: '420px',
                                overflowY: 'auto',
                                background: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '12px',
                                boxShadow: '0 14px 28px rgba(15, 23, 42, 0.15)',
                                zIndex: 200,
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0.75rem 0.9rem',
                                    borderBottom: '1px solid #e2e8f0',
                                    background: '#f8fafc',
                                }}>
                                    <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>Notifications</strong>
                                    <button
                                        onClick={() => markAllReadMutation.mutate()}
                                        disabled={markAllReadMutation.isPending || unreadCount === 0}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            color: unreadCount === 0 ? '#94a3b8' : '#2563eb',
                                            fontSize: '0.75rem',
                                            cursor: unreadCount === 0 ? 'not-allowed' : 'pointer',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Mark all read
                                    </button>
                                </div>

                                {notificationsQuery.isLoading && (
                                    <div style={{ padding: '1rem', color: '#64748b', fontSize: '0.85rem' }}>Loading notifications...</div>
                                )}

                                {notificationsQuery.isError && (
                                    <div style={{ padding: '1rem', color: '#b91c1c', fontSize: '0.85rem' }}>
                                        Failed to load notifications.
                                    </div>
                                )}

                                {!notificationsQuery.isLoading && !notificationsQuery.isError && notificationItems.length === 0 && (
                                    <div style={{ padding: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
                                        No notifications yet.
                                    </div>
                                )}

                                {!notificationsQuery.isLoading && !notificationsQuery.isError && notificationItems.map((notification) => (
                                    <button
                                        key={notification.NotificationID}
                                        onClick={() => handleNotificationClick(notification)}
                                        style={{
                                            width: '100%',
                                            border: 'none',
                                            borderBottom: '1px solid #f1f5f9',
                                            background: notification.IsRead ? '#fff' : '#f8fbff',
                                            textAlign: 'left',
                                            padding: '0.75rem 0.9rem',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                            <div style={{
                                                fontSize: '0.66rem',
                                                fontWeight: 700,
                                                letterSpacing: '0.25px',
                                                color: '#fff',
                                                background: getNotificationBadgeColor(notification.Type),
                                                padding: '2px 7px',
                                                borderRadius: '999px',
                                                textTransform: 'uppercase',
                                            }}>
                                                {(notification.Type || 'NOTICE').replaceAll('_', ' ')}
                                            </div>
                                            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                                                {formatNotificationTime(notification.CreatedAt)}
                                            </span>
                                        </div>
                                        <div style={{ marginTop: '0.35rem', fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>
                                            {notification.Title || 'Notification'}
                                        </div>
                                        <div style={{ marginTop: '0.18rem', fontSize: '0.78rem', color: '#475569' }}>
                                            {notification.Body || ''}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {user && (
                        <div style={{ position: 'relative' }}>
                            <div
                                onClick={() => setShowProfile(!showProfile)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                    fontSize: '0.9rem', color: '#1e293b', cursor: 'pointer',
                                    padding: '0.25rem 0.5rem', borderRadius: '6px'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <div style={{
                                    padding: user?.attributes?.picture ? '0' : '0.35rem',
                                    background: user?.attributes?.picture ? 'transparent' : '#e2e8f0',
                                    borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    overflow: 'hidden',
                                    width: '32px', height: '32px'
                                }}>
                                    {user?.attributes?.picture ? (
                                        <img
                                            src={user.attributes.picture}
                                            alt="Profile"
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <User size={16} color="#475569" />
                                    )}
                                </div>
                                <span style={{ fontWeight: 600 }}>{userName}</span>
                                <ChevronDown size={14} color="#64748b" />
                            </div>

                            {/* Profile Dropdown */}
                            {showProfile && (
                                <div style={{
                                    position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                                    background: 'white', border: '1px solid #ddd', borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', width: '250px', zIndex: 100,
                                    overflow: 'hidden'
                                }}>
                                    <div style={{ padding: '1rem', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <div
                                            style={{
                                                width: '50px', height: '50px', minWidth: '50px', borderRadius: '50%', overflow: 'hidden', background: '#e2e8f0',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', cursor: 'pointer'
                                            }}
                                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                            title="Click to upload profile picture"
                                        >
                                            {isUploadingImage ? (
                                                <RefreshCw size={20} color="#475569" style={{ animation: 'spin 1s linear infinite' }} />
                                            ) : user?.attributes?.picture ? (
                                                <img src={user.attributes.picture} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <User size={24} color="#475569" />
                                            )}
                                        </div>
                                        <div style={{ overflow: 'hidden' }}>
                                            <div style={{ fontWeight: 'bold', fontSize: '1rem', color: '#202124', marginBottom: '0.25rem', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                                {userName}
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: '#5f6368', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user.email}</div>
                                        </div>
                                    </div>
                                    <div style={{ padding: '1rem', background: '#f8f9fa' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                                            Profile
                                        </div>
                                        <div style={{ marginBottom: '0.75rem', fontWeight: 500, color: '#3c4043' }}>
                                            {userProfile}
                                        </div>

                                        <div style={{ fontSize: '0.8rem', color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.5rem' }}>
                                            Division
                                        </div>
                                        <div style={{ fontWeight: 500, color: '#1a73e8' }}>
                                            {divisionName || 'N/A'}
                                        </div>
                                    </div>
                                    <div style={{ padding: '0.5rem' }}>
                                        <button
                                            onClick={handleSignOut}
                                            style={{
                                                width: '100%', padding: '0.75rem', border: 'none', background: 'white',
                                                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                color: '#d93025', fontSize: '0.9rem', borderRadius: '4px'
                                            }}
                                            onMouseEnter={(e) => e.target.style.background = '#fce8e6'}
                                            onMouseLeave={(e) => e.target.style.background = 'white'}
                                        >
                                            <LogOut size={18} /> Sign Out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', padding: '0 1rem 1rem 1rem', gap: '0.75rem' }}>
                <Sidebar
                    selectedDivision={selectedDivision}
                    onDivisionSelect={setSelectedDivision}
                />
                {/* Main Content */}
                <main style={{ flex: 1, padding: '2rem', overflowY: 'auto', backgroundColor: '#fff', borderRadius: '20px', position: 'relative' }}>
                    <Outlet context={{ selectedDivision }} />
                </main>
            </div>

            {cropImageSrc && (
                <ImageCropperModal
                    imageSrc={cropImageSrc}
                    onCropComplete={handleCropSubmit}
                    onCancel={() => setCropImageSrc(null)}
                />
            )}
        </div>
    );
};
export default Layout;
